import React, { useState, useEffect } from "react";
import {
	Box,
	Paper,
	TextField,
	Button,
	Typography,
	Alert,
	IconButton,
	InputAdornment,
	CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff, LockReset } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Logo from "../assets_download_placeholder.png";

const ResetPassword = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const token = searchParams.get("token");

	const [formData, setFormData] = useState({
		newPassword: "",
		confirmPassword: "",
	});

	const [showPassword, setShowPassword] = useState({
		new: false,
		confirm: false,
	});

	const [loading, setLoading] = useState(false);
	const [validatingToken, setValidatingToken] = useState(true);
	const [tokenValid, setTokenValid] = useState(false);
	const [errors, setErrors] = useState({});

	useEffect(() => {
		if (!token) {
			setValidatingToken(false);
			return;
		}

		// You could add token validation here if needed
		setTokenValid(true);
		setValidatingToken(false);
	}, [token]);

	const validateForm = () => {
		const newErrors = {};

		if (!formData.newPassword) {
			newErrors.newPassword = "New password is required";
		} else if (formData.newPassword.length < 8) {
			newErrors.newPassword = "Password must be at least 8 characters";
		}

		if (!formData.confirmPassword) {
			newErrors.confirmPassword = "Please confirm your new password";
		} else if (formData.newPassword !== formData.confirmPassword) {
			newErrors.confirmPassword = "Passwords do not match";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		setLoading(true);

		try {
			const response = await axios.post(
				"https://parallexsms.approot.ng/reset_password.php",
				{
					token: token,
					newPassword: formData.newPassword,
				},
				{
					headers: {
						"Content-Type": "application/json",
					},
				}
			);

			if (response.data.success) {
				toast.success("Password reset successfully!");
				setTimeout(() => {
					navigate("/login");
				}, 2000);
			} else {
				setErrors({ submit: response.data.message });
			}
		} catch (error) {
			console.error("Reset password error:", error);
			setErrors({ submit: "Failed to reset password. Please try again." });
		} finally {
			setLoading(false);
		}
	};

	const handleChange = (field) => (e) => {
		const value = e.target.value;
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));

		// Clear error for this field
		if (errors[field]) {
			setErrors((prev) => ({
				...prev,
				[field]: "",
			}));
		}
	};

	const togglePasswordVisibility = (field) => {
		setShowPassword((prev) => ({
			...prev,
			[field]: !prev[field],
		}));
	};

	if (validatingToken) {
		return (
			<Box
				sx={{
					minHeight: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					bgcolor: "#f5f5f5",
				}}
			>
				<CircularProgress color='warning' />
			</Box>
		);
	}

	if (!token || !tokenValid) {
		return (
			<Box
				sx={{
					minHeight: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					bgcolor: "#f5f5f5",
					p: 3,
				}}
			>
				<Paper
					elevation={3}
					sx={{
						p: 4,
						maxWidth: 500,
						width: "100%",
						textAlign: "center",
					}}
				>
					<Alert severity='error' sx={{ mb: 3 }}>
						Invalid or expired reset link
					</Alert>
					<Button
						onClick={() => navigate("/login")}
						variant='contained'
						sx={{
							backgroundColor: "#DB9E27",
							"&:hover": {
								backgroundColor: "#c5891e",
							},
						}}
					>
						Return to Login
					</Button>
				</Paper>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				minHeight: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				bgcolor: "#f5f5f5",
				p: 3,
			}}
		>
			<Paper
				elevation={3}
				sx={{
					p: 4,
					maxWidth: 500,
					width: "100%",
					borderRadius: 2,
				}}
			>
				<Box sx={{ textAlign: "center", mb: 4 }}>
					<img
						src={Logo}
						alt='Bank Logo'
						style={{ height: 60, marginBottom: 20 }}
					/>
					<LockReset sx={{ fontSize: 48, color: "#DB9E27", mb: 2 }} />
					<Typography variant='h5' fontWeight='bold' gutterBottom>
						Reset Your Password
					</Typography>
					<Typography variant='body2' color='text.secondary'>
						Enter a new password for your account
					</Typography>
				</Box>

				{errors.submit && (
					<Alert severity='error' sx={{ mb: 3 }}>
						{errors.submit}
					</Alert>
				)}

				<form onSubmit={handleSubmit}>
					<TextField
						fullWidth
						label='New Password'
						type={showPassword.new ? "text" : "password"}
						value={formData.newPassword}
						onChange={handleChange("newPassword")}
						error={!!errors.newPassword}
						helperText={errors.newPassword || "Minimum 8 characters"}
						margin='normal'
						required
						InputProps={{
							endAdornment: (
								<InputAdornment position='end'>
									<IconButton
										onClick={() => togglePasswordVisibility("new")}
										edge='end'
									>
										{showPassword.new ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>

					<TextField
						fullWidth
						label='Confirm New Password'
						type={showPassword.confirm ? "text" : "password"}
						value={formData.confirmPassword}
						onChange={handleChange("confirmPassword")}
						error={!!errors.confirmPassword}
						helperText={errors.confirmPassword}
						margin='normal'
						required
						InputProps={{
							endAdornment: (
								<InputAdornment position='end'>
									<IconButton
										onClick={() => togglePasswordVisibility("confirm")}
										edge='end'
									>
										{showPassword.confirm ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>

					<Button
						fullWidth
						variant='contained'
						type='submit'
						disabled={loading}
						sx={{
							mt: 4,
							mb: 2,
							p: 1.5,
							bgcolor: "#DB9E27",
							"&:hover": {
								bgcolor: "#c5891e",
							},
						}}
					>
						{loading ? "Resetting Password..." : "Reset Password"}
					</Button>
				</form>

				<Box sx={{ textAlign: "center", mt: 3 }}>
					<Button onClick={() => navigate("/login")} sx={{ color: "#DB9E27" }}>
						Back to Login
					</Button>
				</Box>
			</Paper>
		</Box>
	);
};

export default ResetPassword;
